import * as Yup from 'yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
// eslint-disable-next-line import/no-extraneous-dependencies
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Alert,
  Button,
  Dialog,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { EmailRegex } from 'src/utils/data/regex';

import { useTranslate } from 'src/locales';
import { ActivationTerms } from 'src/services/admin/admin.types';
import { useCreateTenantMutation } from 'src/services/admin/admin.service';

import FormProvider, { RHFTextField } from 'src/components/hook-form';
import CompanyIdentityFields, {
  companyIdentitySchema,
} from 'src/components/company-identity/company-identity-fields';

import { TenantType } from 'src/types/enums';
import { PayPeriod } from 'src/types/payment';

import ActivationTermsFields from './activation-terms-fields';

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

/**
 * Sales-led tenant creation. A super admin enters the company, the contact person and the agreed
 * commercial terms; the contact receives the activation link and sets their own password there.
 */
export default function CreateTenantDialog({ open, onClose }: Props) {
  const { t } = useTranslate();
  const router = useRouter();

  const [tenantType, setTenantType] = useState<TenantType>(TenantType.Recruiter);
  const [terms, setTerms] = useState<ActivationTerms>({
    product_identifier: '',
    billing_period: PayPeriod.Monthly,
    seats: 1,
    promo_code: '',
  });

  const [createTenant, { isLoading }] = useCreateTenantMutation();

  const Schema = Yup.object().shape({
    ...companyIdentitySchema(t),
    first_name: Yup.string().required(t('auth.validation.firstName.required')),
    last_name: Yup.string().required(t('auth.validation.lastName.required')),
    email: Yup.string()
      .required(t('auth.validation.email.required'))
      .matches(EmailRegex, t('auth.validation.email.valid')),
    phone: Yup.string(),
  });

  const formMethods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      country_code: 'AU',
      company_name: '',
      tax_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
    },
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = formMethods;

  const handleClose = () => {
    reset();
    setTerms({ product_identifier: '', billing_period: PayPeriod.Monthly, seats: 1, promo_code: '' });
    onClose();
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const request = await createTenant({
        tenant_type: tenantType,
        company_name: data.company_name,
        tax_id: data.tax_id,
        country_code: data.country_code,
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone || undefined,
        ...terms,
        promo_code: terms.promo_code || undefined,
      }).unwrap();

      enqueueSnackbar(t('restricted.organizations.create_tenant.api.success'), { variant: 'success' });

      handleClose();
      router.push(`${paths.dashboard.restricted.tenants.root}/${request.organization_id}`);
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message ?? t('restricted.organizations.create_tenant.api.default_error'),
        { variant: 'error' }
      );
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <FormProvider methods={formMethods} onSubmit={onSubmit}>
        <DialogTitle>{t('restricted.organizations.create_tenant.title')}</DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('restricted.organizations.create_tenant.description')}
          </Alert>

          <Grid container spacing={2} sx={{ mt: 0 }}>

            <Grid item xs={12}>
              <Typography variant="subtitle2">
                {t('restricted.organizations.create_tenant.company_section')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="create-tenant-type-label">
                  {t('restricted.organizations.create_tenant.tenant_type')}
                </InputLabel>
                <Select
                  labelId="create-tenant-type-label"
                  label={t('restricted.organizations.create_tenant.tenant_type')}
                  value={tenantType}
                  onChange={(e) => {
                    setTenantType(e.target.value as TenantType);
                    // plans are tenant specific - drop a selection that no longer applies
                    setTerms((current) => ({ ...current, product_identifier: '' }));
                  }}
                  inputProps={{ 'data-testid': 'create-tenant-type-select' }}
                >
                  <MenuItem value={TenantType.Recruiter}>
                    {t('enums.tenant.recruiter')}
                  </MenuItem>
                  <MenuItem value={TenantType.Client}>
                    {t('enums.tenant.client')}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <CompanyIdentityFields />

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                {t('restricted.organizations.create_tenant.contact_section')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <RHFTextField name="first_name" size="small" label={t('auth.form-labels.first-name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RHFTextField name="last_name" size="small" label={t('auth.form-labels.last-name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RHFTextField name="email" size="small" label={t('auth.form-labels.email')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RHFTextField name="phone" size="small" label={t('auth.form-labels.phone')} />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                {t('restricted.organizations.create_tenant.licence_section')}
              </Typography>
            </Grid>

            <ActivationTermsFields
              tenantType={tenantType}
              value={terms}
              onChange={setTerms}
            />
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            disabled={!isValid || !terms.product_identifier}
            data-testid="create-tenant-submit-btn"
          >
            {t('restricted.organizations.create_tenant.submit')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
